import React, { useState, useEffect, useCallback } from "react";
import Layout from "containers/LayoutV2";
import LoiNhuanApi from "service/BaoCao/LoiNhuanApi";
import { SearchBarLoiNhuan } from "./SearchBarLoiNhuan";
import { SummarySectionLoiNhuan } from "./SummarySectionLoiNhuan";
import { ChartSectionLoiNhuan } from "./ChartSectionLoiNhuan";
import { TangTruongLoiNhuan } from "./TangTruongLoiNhuan";
import { getKySoSanh } from "./periodUtils";
import { pushNotification } from "utils/notifications";
import cauHinh from "utils/cauHinh";
import { format, startOfYear, endOfYear } from "date-fns";
import TraGopApi from "service/traGopApi";
import { TableDanhSachLoiNhuan } from "./ChiTiet/TableDanhSachLoiNhuan";
import UserApi from "service/userApi";

export const ThongKeLoiNhuan = () => {
  const [filters, setFilters] = useState({
    dtFrom: null,
    dtTo: null,
    timeValue: "all",
    year: new Date().getFullYear(),
    isCustom: false,
  });

  const [loadingData, setLoadingData] = useState({
    summary: true,
    chart: true,
    tangTruong: true,
  });
  const [summaryData, setSummaryData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [kySoSanh, setKySoSanh] = useState(null);
  const [loiNhuanKyTruoc, setLoiNhuanKyTruoc] = useState(0);
  const [modalState, setModalState] = useState({
    show: false,
    type: "",
    title: "",
  });
  const [hasPermission, setHasPermission] = useState(null);
  const [RoleLoiNhuanIn, setRoleLoiNhuanIn] = useState(false);
  const [systemConfig, setSystemConfig] = useState({});

  const fetchPermission = () => {
    try {
      UserApi.getAllQuyen()
        .then((res) => {
          const detailedRoles = res.data.map((item) =>
            item.ModuleCode.toLowerCase(),
          );
          const hasList = detailedRoles.includes("loinhuan");
          setHasPermission(hasList);
          if (hasList) {
            setRoleLoiNhuanIn(
              detailedRoles.includes("loinhuan_indanhsachloinhuan"),
            );
          }
        })
        .catch((err) => {
          console.error("Lỗi load quyền chi tiết:", err);
          setHasPermission(false);
        });
    } catch (err) {
      console.error("Lỗi fetchPermission:", err);
      setHasPermission(false);
    }
  };

  const fetchConfig = () => {
    TraGopApi.getConfigSys()
      .then((res) => {
        if (res.status === 200 && res.data && res.data.length > 0) {
          const configObj = {};
          res.data.forEach((item) => {
            if (item.keyConfig) {
              configObj[item.keyConfig] = item.valueConfig;
            }
          });

          setSystemConfig(configObj);
        }
      })
      .catch((err) => {
        console.error("Lỗi lấy cấu hình hệ thống:", err);
      });
  };

  // Khối "Tăng trưởng lợi nhuận" là phần riêng của nhà bật isMoney88.
  // Nhà không bật: báo cáo lợi nhuận giữ nguyên như bản chung.
  const isMoney88 = Object.keys(systemConfig).some(
    (k) =>
      k.toUpperCase() === cauHinh.sKey.IsMoney88.toUpperCase() &&
      systemConfig[k] === "1"
  );

  const fetchData = useCallback(() => {
    setLoadingData({ summary: true, chart: true, tangTruong: true });
    let dateFrom = filters.dtFrom;
    let dateTo = filters.dtTo;
    if (!dateFrom || !dateTo) {
      dateFrom = startOfYear(new Date());
      dateTo = endOfYear(new Date());
    }
    const strFrom = format(dateFrom, "dd/MM/yyyy");
    const strTo = format(dateTo, "dd/MM/yyyy");

    // Không bật isMoney88 thì bỏ hẳn kỳ so sánh, không gọi thêm API kỳ trước.
    const kyInfo = isMoney88
      ? getKySoSanh({
          dtFrom: dateFrom,
          dtTo: dateTo,
          timeValue: filters.timeValue,
          isCustom: filters.isCustom,
        })
      : null;
    setKySoSanh(kyInfo);

    const summaryPromise = LoiNhuanApi.getSummary(strFrom, strTo);
    const chartPromise = LoiNhuanApi.getChart(strFrom, strTo);
    const kyTruocPromise = kyInfo
      ? LoiNhuanApi.getSummary(
          format(kyInfo.kyTruoc.from, "dd/MM/yyyy"),
          format(kyInfo.kyTruoc.to, "dd/MM/yyyy"),
        )
      : Promise.resolve(null);

    Promise.all([summaryPromise, chartPromise, kyTruocPromise])
      .then(([summaryRes, chartRes, kyTruocRes]) => {
        if (summaryRes.status === 200 && summaryRes.data) {
          setSummaryData(summaryRes.data);
        } else {
          setSummaryData([]);
        }
        if (chartRes.status === 200 && chartRes.data) {
          setChartData(chartRes.data);
        } else {
          setChartData([]);
        }
        if (
          kyTruocRes &&
          kyTruocRes.status === 200 &&
          kyTruocRes.data &&
          kyTruocRes.data.length > 0
        ) {
          setLoiNhuanKyTruoc(kyTruocRes.data[0].LoiNhuanRong);
        } else {
          setLoiNhuanKyTruoc(0);
        }
      })
      .catch((err) => {
        console.error(err);
        pushNotification("Tải dữ liệu báo cáo thất bại!", "error");
      })
      .finally(() => {
        setLoadingData({ summary: false, chart: false, tangTruong: false });
      });
  }, [filters, isMoney88]);

  useEffect(() => {
    document.title = "Báo cáo lợi nhuận | Phần mềm quản lý cầm đồ Morman";
    fetchPermission();
    fetchConfig();
  }, []);

  useEffect(() => {
    if (hasPermission) {
      fetchData();
    }
  }, [hasPermission, fetchData]);

  const handleApplyFilter = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSummaryClick = (type, title) => {
    setModalState({ show: true, type, title });
  };

  if (hasPermission === false) {
    return (
      <Layout>
        <h2 className="text-danger">
          <div className="custom-table-row">
            <div
              className="custom-table-cell text-center red"
              style={{ width: "100%" }}
            >
              Bạn không có quyền truy cập trang này!
            </div>
          </div>
        </h2>
      </Layout>
    );
  }

  return (
    <Layout>
      <div id="form-repeater" className="p-2">
        <div className="row header justify-content-start mb-1 ml-1">
          <img src="/app-assets/images/icon-thong-ke.png" alt="Icon" />
          <h1 className="text-info-home">Báo Cáo Lợi Nhuận</h1>
        </div>
        <div className="card border-light mb-1 card-v2">
          <div className="card-body card-body-v2">
            <SearchBarLoiNhuan onApply={handleApplyFilter} />
          </div>
        </div>
        <div className="card border-light mb-1 card-v2">
          <div className="card-body card-body-v2">
            <SummarySectionLoiNhuan
              data={summaryData}
              loading={loadingData.summary}
              onItemClick={handleSummaryClick}
              config={systemConfig}
              isMoney88={isMoney88}
              loiNhuanKyTruoc={loiNhuanKyTruoc}
              kyTruoc={kySoSanh && kySoSanh.kyTruoc}
            />
          </div>
        </div>
        {isMoney88 ? (
          <div className="d-flex flex-wrap" style={{ gap: "0 8px" }}>
            <div
              className="card border-light mb-1 card-v2"
              style={{ flex: "3 1 520px", minWidth: 0 }}
            >
              <div className="card-body card-body-v2">
                <ChartSectionLoiNhuan
                  data={chartData}
                  loading={loadingData.chart}
                />
              </div>
            </div>
            <div
              className="card border-light mb-1 card-v2"
              style={{ flex: "2 1 460px", minWidth: 0 }}
            >
              <div className="card-body card-body-v2">
                <TangTruongLoiNhuan
                  kyNay={kySoSanh && kySoSanh.kyNay}
                  kyTruoc={kySoSanh && kySoSanh.kyTruoc}
                  loiNhuanKyNay={
                    summaryData && summaryData.length > 0
                      ? summaryData[0].LoiNhuanRong
                      : 0
                  }
                  loiNhuanKyTruoc={loiNhuanKyTruoc}
                  loading={loadingData.tangTruong}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="card border-light mb-1 card-v2">
            <div className="card-body card-body-v2">
              <ChartSectionLoiNhuan
                data={chartData}
                loading={loadingData.chart}
              />
            </div>
          </div>
        )}
      </div>

      {modalState.show && (
        <TableDanhSachLoiNhuan
          show={modalState.show}
          onHide={() => setModalState({ ...modalState, show: false })}
          type={modalState.type}
          title={modalState.title}
          filters={filters}
          RoleLoiNhuanIn={RoleLoiNhuanIn}
        />
      )}
    </Layout>
  );
};
